import { getAuthSession } from '../services/authHttp.service';
import {
  CapabilityName,
  checkAuthLoader,
  getAuthToken,
  hasCapability,
  isAuthSessionHydrated,
  setAuthSession
} from './auth';

let inFlight: { token: string; promise: Promise<void> } | null = null;

/**
 * Fetch `GET auth/session/` for the current token and apply it with
 * `setAuthSession`, once per token. Concurrent callers share one request.
 *
 * - `{ authenticated: false }` clears auth (handled by `setAuthSession`).
 * - A failed fetch (network error, bad payload) applies nothing: capabilities
 *   stay closed and the user is NOT signed out. The next call retries.
 * - A response that arrives after the token changed is dropped.
 */
export function loadAuthSession(): Promise<void> {
  const token = getAuthToken();
  if (!token || isAuthSessionHydrated()) {
    return Promise.resolve();
  }

  if (inFlight && inFlight.token === token) {
    return inFlight.promise;
  }

  const promise: Promise<void> = getAuthSession()
    .then((payload) => {
      if (payload && getAuthToken() === token) {
        setAuthSession(payload);
      }
    })
    .catch(() => undefined)
    .finally(() => {
      if (inFlight?.promise === promise) {
        inFlight = null;
      }
    });

  inFlight = { token, promise };
  return promise;
}

const redirect = (to: string): Response =>
  new Response(null, { status: 302, headers: { Location: to } });

/**
 * Route loader that admits only users whose server session grants `name`.
 * It awaits the session first, so a role edited in browser storage cannot
 * render a gated route, even briefly.
 */
export function capabilityLoader(name: CapabilityName) {
  return async (): Promise<Response | null> => {
    const signinRedirect = checkAuthLoader();
    if (signinRedirect) {
      return signinRedirect;
    }

    await loadAuthSession();

    // The server may have reported the session as unauthenticated.
    const afterSession = checkAuthLoader();
    if (afterSession) {
      return afterSession;
    }

    return hasCapability(name) ? null : redirect('/data-flows');
  };
}
